import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

// Loading spinner
  const LoadingSpinner = () => (
    <svg
      className="animate-spin h-4 w-4 text-current"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

export const Button = ({
  type = "button",
  value = "Button",
  styles = {},
  handleClick,
  className = "",
  disabled = false,
  loading = false,
  variant = "primary", // primary, secondary, outline, danger, success
  size = "medium", // small, medium, large
  icon = null,
  iconPosition = "left", // left, right
  fullWidth = false,
}) => {
  // Size classes
  const sizeClasses = {
    small: "px-3 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg",
  };

  // Variant classes with enhanced styling
  const variantClasses = {
    primary: "bg-gradient-to-r from-[#0b3a5c] to-[#135e96] text-white hover:from-[#135e96] hover:to-[#0b3a5c] focus:ring-[#135e96] shadow-lg hover:shadow-xl relative overflow-hidden group",
    secondary: "bg-gradient-to-r from-gray-700 to-gray-800 text-white hover:from-gray-800 hover:to-gray-900 focus:ring-gray-500 shadow-lg hover:shadow-xl relative overflow-hidden group",
    outline:
      "border-2 border-[#135e96] text-[#135e96] bg-white hover:bg-gradient-to-r hover:from-[#135e96] hover:to-[#0b3a5c] hover:text-white focus:ring-[#135e96] shadow-md hover:shadow-lg relative overflow-hidden group transition-all duration-300",
    danger: "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 focus:ring-red-500 shadow-lg hover:shadow-xl relative overflow-hidden group",
    success: "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-500 shadow-lg hover:shadow-xl relative overflow-hidden group",
  };

  // Base classes with enhanced animations
  const baseClasses =
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2";

  // Disabled classes
  const disabledClasses = "opacity-50 cursor-not-allowed hover:shadow-sm";

  const buttonClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${disabled || loading ? disabledClasses : ""}
    ${fullWidth ? "w-full" : ""}
    ${className}
  `.trim();

  return (
    <motion.button
      type={type}
      className={buttonClasses}
      style={styles}
      onClick={!disabled && !loading ? handleClick : undefined}
      disabled={disabled || loading}
      whileHover={!disabled && !loading ? { scale: 1.03, y: -2 } : {}}
      whileTap={!disabled && !loading ? { scale: 0.97 } : {}}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      {/* Shine effect overlay */}
      {!disabled && !loading && (
        <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
      )}
      
      {loading && <LoadingSpinner />}
      {!loading && icon && iconPosition === "left" && (
        <motion.span 
          className="flex-shrink-0 relative z-10"
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.5 }}
        >
          {icon}
        </motion.span>
      )}

      <span className={`relative z-10 ${loading ? "opacity-75" : ""}`}>
        {loading ? "Loading..." : value}
      </span>

      {!loading && icon && iconPosition === "right" && (
        <motion.span 
          className="flex-shrink-0 relative z-10"
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.5 }}
        >
          {icon}
        </motion.span>
      )}
    </motion.button>
  );
};

Button.propTypes = {
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  value: PropTypes.string,
  styles: PropTypes.object,
  handleClick: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "outline",
    "danger",
    "success",
  ]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  fullWidth: PropTypes.bool,
};

